<template>

  <div>

    <div class="d-flex align-center justify-space-between mb-4">

      <div
        style="font-size: 1.2rem; font-weight: 700;"
      >
        Add keywords <span style="padding:2px 6px; border: 1px solid var(--v-grayColor-base); border-radius: 18px; font-weight: normal;">{{ keywords.length }} / 100</span>
      </div>

      <div class="lang-loc-fields">
        <v-autocomplete dense solo hide-details="auto"
          append-icon="keyboard_arrow_down"
          :items="availableLocations"
          item-text="location_name"
          item-value="location_code"
          v-model="selectedLocation"
          color="grayColor">
        </v-autocomplete>
    
        <v-autocomplete dense solo hide-details
          append-icon="keyboard_arrow_down"
          :items="availableLanguages"
          v-model="selectedLanguage"
          item-text="language_name"
          item-value="language_code"
          color="grayColor">
        </v-autocomplete>
      </div>

    </div>

    <div class="mb-2">
      <v-textarea
        outlined
        hide-details
        color="greenColor"
        v-model="keywordsText"
        auto-grow
        row-height="28"
        rows="1"
        placeholder="Enter keywords (separated by line breaks)"
      />
    </div>

    <div class="mb-4">
      <v-tooltip top color="transparent">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" style="display: inline-block;">
            <v-checkbox
              class="subdomains-checkbox"
              style="display: inline-flex;"
              color="#007aff"
              hide-details
              :ripple="false"
              :input-value="!include_subdomains_in_analysis"  
              @change="include_subdomains_in_analysis = !$event"
            >
              <template v-slot:label>
                <div style="color: var(--v-blackColor-base); font-size: 0.9rem;">
                  Do not include sub-domains in analysis
                </div>
              </template>
            </v-checkbox>
          </div>
        </template>
        <div class="white-bg-tooltip small-tooltip" style="color: var(--v-blackColor-base);">
          Only count rankings for URLs on your main site (example.com and www.example.com). Rankings on subdomains (like blog.example.com) will be ignored.
        </div>
      </v-tooltip>
    </div>


    <v-btn
      color="profileAnchorColor"
      class="whiteColor--text mb-3"
      style="text-transform: none;"
      large
      :loading="submitting"
      :disabled="insightSubmitDisabled"
      @click="() => hasUnlimitedAccess?getDataAction():(() => {showCostModal = true})()">
      <span style="font-size: 1.1rem;">{{ submitButtonLabel }}</span>
    </v-btn>
    
    <div
      v-if="!hasUnlimitedAccess"
      class="d-flex align-center gap5"
      style="color: var(--v-gray2Color-base);"
    >
      <img v-if="$vuetify.theme.dark" src="/static/icon_credits_dark_mode.svg" /><img v-else src="/static/icon_credits_light_mode.svg" />
      <span>{{ get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) }} POP Credit{{get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) === 0 ?'s':''}}{{get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) > 1 ?'s':''}}</span>

      <b-dropdown aria-role="list" position="is-bottom-right" hoverable>
        <template #trigger>
          <i class="material-icons" style="color: var(--v-gray9Color-base);">info</i>
        </template>

        <b-dropdown-item custom style="width: 250px;">
          <div>
            <div class="mb-2 font-weight-bold">Pricing</div>
            <div v-for="[tier, price] in Object.entries(KEYWORD_RESEARCH_METRICS_PRICE_TIERS)" :key="tier" class="mb-1 d-flex align-center justify-space-between gap10">
              <span><strong>{{ tier }}</strong> Keywords</span> <strong style="width: 70px;">{{ price }} Credit{{ price > 1 ?'s':'' }}</strong>
            </div>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="modal keyword-data-cost-modal" :class="{'is-active': showCostModal}">
      <div class="modal-background" @click.stop="showCostModal = false"></div>
      <div class="modal-content">
        <div class="box modal-container">
          <div class="modal-content-root">
            <p class="title-font is-size-4 mainColor--text">Get keyword data</p>
            <div class="mainColor--text message-text">
              <div class="mb-2">You’ve selected <span class="font-weight-bold">{{ keywords.length }} keyword{{keywords.length >1 ?'s':''}}</span>.</div>
              <div class="mb-1">We’ll retrieve detailed metrics for {{keywords.length >1 ?'each':'the'}} keyword, including:</div>
              <ul class="mb-2" style="list-style: initial;">
                <li>Keyword Difficulty Score</li>
                <li>Search Volume</li>
                <li>Search Volume trend</li>
                <li>Ranking URL and Position</li>
                <li>Keyword Score</li>
                <li>SEO Competitiveness Index (SCI)</li>
                <li>AllinTitle</li>
                <li>KGR Score</li>
              </ul>
              
              This action will consume <span class="font-weight-bold">{{ get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) }} POP Credit{{get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) >1 ?'s':''}}</span>. Click <span class="font-weight-bold">Get Data</span> to proceed.
            </div>
            <div class="d-flex justify-end gap10 mt-5">
              <v-btn rounded outlined color="mainColor"
                class="text-none"
                @click.stop="() => {
                  showCostModal = false;
                }">
                <span>Cancel</span>
              </v-btn>
              <v-btn rounded color="profileAnchorColor"
                class="whiteColor--text text-none"
                :loading="submitting"
                @click.stop="getDataAction">
                <span>Get data</span>
              </v-btn>
              
            </div>
          </div>
        </div>
        <button
          class="custom-modal-close-btn"
          aria-label="close"
          @click="() => (showCostModal = false)">
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>

  </div>

</template>

<script>
import { getHostName } from '@/utils';
import languages from '@/consts/dataforseo_languages.json';
import dataForSeoLocations from '@/consts/dataforseo_locations.json';
import {
  get_keyword_research_metrics_price_for_num_of_keywords,
  KEYWORD_RESEARCH_METRICS_PRICE_TIERS
} from '@/components/plans_constants.js';


const MAX_KEYWORDS_LIMIT = 100


export default {
  props: {
    site: {},
    campaign: {},
    hasUnlimitedAccess: {},
    isWhiteGloveUser: {},
    isMissionControlSession: {},
    reloadUser: {},
    submitButtonLabel: {
      type: String,
      default: 'Get insights',
    },
    allowWhiteGloveSubmit: {
      type: Boolean,
      default: false,
    },
    afterSuccessNavigate: {
      type: Function,
      default: null,
    },
    isContentOnlyView: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    
  },
  data () {
    return {
      selectedLocation: null,
      selectedLanguage: null,
      include_subdomains_in_analysis: true,

      keywordsText: '',
      keywords: [],

      submitting: false,
      showCostModal: false,
    }
  },
  computed: {
    KEYWORD_RESEARCH_METRICS_PRICE_TIERS() {
      return KEYWORD_RESEARCH_METRICS_PRICE_TIERS
    },
    availableLocations () {
      return dataForSeoLocations.locations;
    },
    availableLanguages () {
      return languages;
    },
    canProceed () {
      if (this.submitting) {
        return false;
      }
      return (this.keywords?.length > 0 && Boolean(this.selectedLocation) && Boolean(this.selectedLanguage))
    },
    insightSubmitDisabled () {
      const wgBlocked = this.isWhiteGloveUser &&
        !this.allowWhiteGloveSubmit &&
        !(this.isMissionControlSession || this.isContentOnlyView)
      return !this.canProceed || wgBlocked
    },
  },
  watch: {
    keywordsText: {
      handler: function (newVal, oldVal) {
        if (newVal != oldVal) {
          // split by comma and line-break, and remove '?' and double spaces
          const cleanNewVal = newVal.replace(',', '\n').split('\n').reduce((acc, curVal) => {
            const keyword = curVal.replace('?', '').replace('.', '').replace(/\u200b/g, '').split(/\s+/).join(" ")
            acc.push(keyword)
            return acc
          }, []).join('\n')

          if (cleanNewVal !== newVal) {
            this.$nextTick(() => {
              this.keywordsText = cleanNewVal
            })
          } else {
            const keywords = this.keywordsText.split('\n').reduce((acc, curVal) => {
              if (curVal.trim()) {
                acc.push(curVal.trim())
              }
              return acc
            }, [])

            this.keywords = keywords.slice(0, MAX_KEYWORDS_LIMIT)
          }
        }
      },
      immediate: true
    }

  },
  methods: {
    get_keyword_research_metrics_price_for_num_of_keywords(num_of_keywords) {
      return get_keyword_research_metrics_price_for_num_of_keywords(num_of_keywords)
    },
    getDataAction () {
      this.showCostModal = false;
      this.submitting = true

      const data = {
        campaignId: this.campaign.id,
        keywords: this.keywords,
        domain: getHostName(this.campaign.targetUrl),
        locationCode: this.selectedLocation,
        languageCode: this.selectedLanguage,
        include_subdomains_in_analysis: this.include_subdomains_in_analysis,
      }
      this.$store.dispatch('createKeywordInsightResearch', data)
      .then(response => {
        if (response.data) {
          if (response.data.status == 'SUCCESS') {
            this.$store.commit('triggerAsyncTasksRefresh');
            this.reloadUser()
            const id = response.data.id
            if (typeof this.afterSuccessNavigate === 'function') {
              const loc = this.afterSuccessNavigate(id)
              if (loc) {
                this.$router.push(loc)
              }
            } else {
              this.$router.push({
                path: `/sites/${this.site.id}/campaign/${this.campaign.id}/keyword-insight/${id}`,
                ...(this.isContentOnlyView
                  ? { query: { fromQuickContentOrdersTab: '1' } }
                  : {}),
              })
            }
          } else if (response.data.status == 'FAILURE') {
            this.$notify({
              group: 'info',
              type: 'warning',
              text: response.data.msg || 'An error happened'
            })
          }
        }
      })
      .finally(() => {
        this.submitting = false
      })
    },
  },
  mounted () {
    if (this.$route.query.initLanguage) {
      this.selectedLanguage = this.$route.query.initLanguage
    } else {
      this.selectedLanguage = this.campaign.languageCode
    }

    if (this.$route.query.initLocation) {
      this.selectedLocation = this.$route.query.initLocation ? Number(this.$route.query.initLocation) : ''
    } else {
      this.selectedLocation = this.campaign.locationCode ? Number(this.campaign.locationCode) : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.v-textarea {
  ::v-deep {
    fieldset {
      border-color: var(--v-gray12Color-base);
    }

    textarea {
      font-size: 1rem;
      margin-top: 12px !important;
      margin-bottom: 12px !important;
      line-height: 34px !important;
    }
  }
}

.lang-loc-fields {
  display: flex;
  flex-direction: row;
  gap: 4px;

  ::v-deep {
    .v-select {
      .v-input__slot {
        box-shadow: none !important;
        background-color: var(--v-gray8Color-base) !important;

        input {
          font-size: .95rem;
          color: var(--v-blackColor-base) !important;
        }
      }
    }
  }
}

::v-deep {
  .subdomains-checkbox {
    margin-top: 0;
    padding-top: 0;
    .v-input__slot{
      padding-left: 0 !important;
    }
  }
}
</style>